import React from "react";
import { useDispatch } from "react-redux";
import { FormattedMessage } from "react-intl";
import cn from "classnames";

import { ITaskEditorProps, ITonesetStaffData, TonesetText } from "../../../../../../common/types";
import { ISelectEvent } from "../../../../../types";
import { setItem, hasItem } from "../../../../../cookie";
import Icon from "../../../../../icon";
import { editTask } from "../../../../../store/plan/actions";
import { post } from "../../../../../actions/general";

import EditorBar, {
    FlexLeft,
    FlexRight,
    Center,
    Changer,
    ChangerPopup,
} from "../../../EditorBar";
import ScoreEditor from "../../../../Score";
import { Range, Select } from "../../../../Form";

import getScore from "../../Common/getScore";
import getTempo from "../../Common/getTempo";
import ChangeColor from "../../Common/ChangeColor";
import ChangeTempo from "../../Common/ChangeTempo";
import useExerciseEditor from "../../Common/useExerciseEditor";
import { SelectWrapper } from "../../common-style";

import messages from "./messages";
import { textOptions } from "./utils";

const TonesetStaffEditor = ({ taskIndex, plan, toggleInlineEditor, publicView, inlineEditorOpen, intl }: ITaskEditorProps) => {
    const dispatch = useDispatch();
    const {
        openChanger,
        playable,
        handleChangeOpen,
        setPopupRef,
        storeScore,
        changeTempo,
        setPlayable,
        exportScore,
    } = useExerciseEditor({ plan, inlineEditorOpen, taskIndex });
    const changeSize = (val: number) => {
        const task = plan.tasks[taskIndex];
        const data = task.data as ITonesetStaffData;
        let size = {
            y: data.size ? data.size.y : 100,
        };
        size.y = val;
        dispatch(editTask({ taskIndex, changingProp: "size", val: size }));
    };
    const changeSizeStop = () => {
        const { size } = plan.tasks[taskIndex].data as ITonesetStaffData;
        dispatch(post("melody-table-size", {
            planId: plan._id,
            index: taskIndex,
            size,
        }));
    };

    const changePosition = (val: number) => {
        dispatch(editTask({ taskIndex, changingProp: "position", val }));
    };

    const changePositionStop = () => {
        const data = plan.tasks[taskIndex].data as ITonesetStaffData;
        dispatch(post("melody-table-position", {
            planId: plan._id,
            index: taskIndex,
            position: data.position,
        }));
    };

    const changeMethod = ({ target: { value } }: ISelectEvent<TonesetText>) => {
        dispatch(editTask({ taskIndex, changingProp: "method", val: value }));
        dispatch(post("toneset-method", {
            planId: plan._id,
            index: taskIndex,
            method: value,
        }));
    };

    const task = plan.tasks[taskIndex];
    const data = task.data as ITonesetStaffData;
    const { position } = data;
    let size = data.size;
    const score = getScore(plan, task)!;
    if (!data.size) size = { y: 100 };
    const forwardProps = {
        plan,
        taskIndex,
        openChanger,
        handleChangeOpen,
        setPopupRef,
    }
    return (
        <div>
            {inlineEditorOpen && (
                <ScoreEditor
                    taskType="melodyTable"
                    score={JSON.parse(JSON.stringify(score || {}))}
                    tempo={getTempo(plan, task)}
                    setScore={storeScore}
                    changeTempo={changeTempo}
                />
            )}
            {!publicView && (
                <EditorBar>
                    <FlexLeft>
                        <Changer active={openChanger === "sizeY"}>
                            <div
                                onClick={e => {
                                    e.stopPropagation();
                                    handleChangeOpen("sizeY");
                                }}
                            >
                                <Icon i="height" />
                            </div>
                            {openChanger === "sizeY" && (
                                <ChangerPopup setRef={setPopupRef}>
                                    <Range
                                        scale={1}
                                        value={size.y}
                                        onChange={changeSize}
                                        onStop={changeSizeStop}
                                        min={30}
                                        max={200}
                                        step={1}
                                        label={<FormattedMessage {...messages.height} />}
                                        name="y"
                                    />
                                </ChangerPopup>
                            )}
                        </Changer>
                        <Changer active={openChanger === "position"}>
                            <div
                                onClick={e => {
                                    e.stopPropagation();
                                    handleChangeOpen("position");
                                }}
                            >
                                <Icon i="position-down" />
                            </div>
                            {openChanger === "position" && (
                                <ChangerPopup setRef={setPopupRef}>
                                    <Range
                                        value={position || 0}
                                        onChange={changePosition}
                                        onStop={changePositionStop}
                                        min={-150}
                                        scale={1}
                                        max={200}
                                        step={2}
                                        label={<FormattedMessage {...messages.position} />}
                                        name="y"
                                    />
                                </ChangerPopup>
                            )}
                        </Changer>
                    </FlexLeft>
                    <Center
                        disabled={(inlineEditorOpen && !playable)}
                        tooltip={!playable && inlineEditorOpen && (
                            <FormattedMessage
                                {...(score?.keyChanges && score.keyChanges.length > 0
                                    ? messages.tonesetStaffNoKeyChange
                                    : messages.createScoreTooltip
                                )}
                            />
                        )}
                        onClick={() => {
                            toggleInlineEditor(!inlineEditorOpen);
                            setPlayable(true);
                            if (inlineEditorOpen) {
                                exportScore();
                            }
                            setItem("melodyTableDone", "1", 60 * 60 * 24 * 365, "/");
                        }}
                        className={cn({
                            pulsate: !(inlineEditorOpen && !playable) &&
                                !hasItem("melodyTableDone") &&
                                !task.ref
                        })}
                    >
                        {inlineEditorOpen ? (
                            <FormattedMessage {...messages.stopEdit} />
                        ) : (
                            <FormattedMessage {...messages.editScore} />
                        )}
                    </Center>
                    <FlexRight>
                        <SelectWrapper>
                            <div>
                                <FormattedMessage {...messages.method} />:
                            </div>
                            <Select
                                dark
                                name="method"
                                options={textOptions(intl!)}
                                value={data.method || TonesetText.Solfa}
                                onChange={changeMethod}
                                noDelete
                            />
                        </SelectWrapper>
                        <ChangeTempo {...forwardProps} />
                        {score && !score.settings.colors && (
                            <ChangeColor
                                {...forwardProps}
                                color={data?.color}
                            />
                        )}
                    </FlexRight>
                </EditorBar>
            )}
        </div>
    );
}

export default TonesetStaffEditor;
